/**
 * idb/idb-core.js
 * ---------------------------------------------------------------------------
 * The RAW IndexedDB layer — plain async functions over the global `indexedDB`,
 * with NO React and NO worker protocol. Runs on the MAIN THREAD (the Web Worker
 * was removed — it gave negligible gain and required build-sensitive Vite
 * `?worker&url` + cross-origin bootstrap). `idb-client.js` calls these directly.
 * Independently unit-tested in `tests/idb-core.spec.js` against fake-indexeddb.
 *
 * Schema note
 * -----------
 * MAA's real database is `assistantBotDB`, created for the app by
 * `@thd-olt-global/thd-storage-utils` from the config in `AssistantOrchestrator`
 * (version 2): a `sessions` store keyed on `sessionId` and a `messages` store
 * keyed on `id` with `autoIncrement: true`. This module opens its OWN connection
 * to that same DB, so its declared version and store shapes MUST match, or the
 * two openers deadlock / throw `VersionError`. We therefore mirror v2 exactly
 * here. `onupgradeneeded` is non-destructive (creates a store only if missing),
 * so whichever opener reaches the DB first produces an identical schema. Do NOT
 * bump the version here without a matching migration — it would rev live DBs.
 *
 * (The v8.2.0 runtime has no `images` store — image uploads are kept in memory
 * as base64 — so no image store/ops are declared.)
 */

const DB_NAME = 'assistantBotDB';
const DB_VERSION = 2;

/** Object-store names, referenced everywhere instead of raw strings. */
export const STORES = {
  MESSAGES: 'messages',
  SESSIONS: 'sessions',
};

/**
 * A single, cached connection promise. The worker is long-lived and
 * single-threaded, so opening the DB once and reusing the handle avoids
 * repeated `open()` round-trips (each of which can fire `onblocked`).
 */
let dbPromise = null;

/** Open (or reuse) the IndexedDB connection. Resolves with the `IDBDatabase`. */
export const openDatabase = () => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    // Runs only when the DB is created or its version increases. We ensure both
    // stores exist without touching any that are already present.
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORES.MESSAGES)) {
        db.createObjectStore(STORES.MESSAGES, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORES.SESSIONS)) {
        db.createObjectStore(STORES.SESSIONS, { keyPath: 'sessionId' });
      }
    };

    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
  });

  return dbPromise;
};

/** Read every record in `storeName`. Resolves with an array (never null). */
export const getAll = (storeName) => openDatabase().then((db) => new Promise((resolve, reject) => {
  const tx = db.transaction(storeName, 'readonly');
  const request = tx.objectStore(storeName).getAll();
  request.onsuccess = () => resolve(request.result || []);
  request.onerror = () => reject(request.error);
}));

/** Insert a brand-new record. Rejects if a record with the same key exists. */
export const add = (storeName, record) => openDatabase().then((db) => new Promise((resolve, reject) => {
  const tx = db.transaction(storeName, 'readwrite');
  tx.objectStore(storeName).add(record);
  // Resolve on `oncomplete`, not on the request's `onsuccess`: the write is only
  // durable once the transaction commits.
  tx.oncomplete = () => resolve(record);
  tx.onerror = () => reject(tx.error);
}));

/** Insert-or-replace a record (IndexedDB `put`). Used for updates. */
export const update = (storeName, record) => openDatabase().then((db) => new Promise((resolve, reject) => {
  const tx = db.transaction(storeName, 'readwrite');
  tx.objectStore(storeName).put(record);
  tx.oncomplete = () => resolve(record);
  tx.onerror = () => reject(tx.error);
}));

/** Insert-or-replace — same as `update`, aliased for image-store readability. */
export const put = update;

/** Read one record by key. Resolves with the record or `null` if absent. */
export const get = (storeName, key) => openDatabase().then((db) => new Promise((resolve, reject) => {
  const tx = db.transaction(storeName, 'readonly');
  const request = tx.objectStore(storeName).get(key);
  request.onsuccess = () => resolve(request.result ?? null);
  request.onerror = () => reject(request.error);
}));

/* Cursor-driven operations — moved here from a worker-driven callback pattern.
 * See .claude/context/persistence.md. */

/**
 * Add `record` only if no existing record matches it on `matchField`.
 * Replaces `utils/idb-message-dedup.js#addIfMessageIdAbsent`.
 *
 * The scan and the conditional add share ONE `readwrite` transaction. Because
 * IndexedDB serializes overlapping `readwrite` transactions (even across tabs),
 * this remains a safe cross-tab "insert if new" primitive.
 *
 * @returns {Promise<boolean>} true if the record was inserted, false if a
 *   matching record already existed.
 */
export const insertIfAbsent = (storeName, record, matchField) => openDatabase()
  .then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    let inserted = false;

    const request = store.openCursor();
    request.onsuccess = (event) => {
      const cursor = event.target.result;

      if (!cursor) {
        // Walked the whole store with no match -> add within THIS transaction.
        store.add(record);
        inserted = true;
        return;
      }

      if (cursor.value?.[matchField] === record[matchField]) {
        // Duplicate found. Stop walking (don't call continue) — the record is
        // left untouched and the transaction commits with no change.
        return;
      }

      cursor.continue();
    };
    request.onerror = () => reject(request.error);

    tx.oncomplete = () => resolve(inserted);
    tx.onerror = () => reject(tx.error);
  }));

/**
 * Delete messages from the `messages` store.
 * Replaces `useMessageHistory#clearMessageHistory`.
 *
 * @param {{ keepInitial?: boolean }} [opts] - when true (default) the row with
 *   `isInitialMessage === true` is preserved, matching the original behavior.
 */
export const clearMessages = ({ keepInitial = true } = {}) => openDatabase()
  .then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.MESSAGES, 'readwrite');
    const store = tx.objectStore(STORES.MESSAGES);

    const request = store.openCursor();
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) return; // done
      const isInitial = cursor.value?.isInitialMessage === true;
      if (!(keepInitial && isInitial)) cursor.delete();
      cursor.continue();
    };
    request.onerror = () => reject(request.error);

    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  }));

/**
 * Prune the `messages` store on startup.
 * Replaces `useMessageHistory#cleanIndexDb`.
 *
 * Rules (identical to the original):
 *   - Keep the FIRST initial message; delete any further duplicate initials.
 *   - Delete any message whose `sessionId` differs from the current session.
 *   - For b2b customers, blank out the suggested questions on the kept initial.
 *
 * @param {{ sessionId: string, customerType?: string }} opts
 */
export const cleanOrphans = ({ sessionId, customerType }) => openDatabase()
  .then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.MESSAGES, 'readwrite');
    const store = tx.objectStore(STORES.MESSAGES);
    let initialMessageFound = false;

    const request = store.openCursor();
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) return; // done

      const value = cursor.value;
      const isDuplicateInitial = value.isInitialMessage && initialMessageFound;

      if (value.isInitialMessage && !initialMessageFound) {
        initialMessageFound = true;
        const hasSuggested = value.content?.relatedQuestions?.suggestedQuestions?.length;
        if (customerType === 'b2b' && hasSuggested) {
          cursor.update({
            ...value,
            content: {
              ...value.content,
              relatedQuestions: {
                suggestedQuestions: [],
                followUpQuestions: [],
                clarifyingQuestions: [],
              },
            },
            updatedAt: Date.now(),
          });
        }
      }

      if (value.sessionId !== sessionId || isDuplicateInitial) {
        cursor.delete();
      }
      cursor.continue();
    };
    request.onerror = () => reject(request.error);

    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  }));
